import { useState, type JSX } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { PatientVisitDto, TrialVisitDto, TrialVisitContact } from '@ctms/contracts';
import { visitLabel } from '@ctms/shared';
import { Building2, Home, Lock, Phone, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../../api/client.js';
import { patientsApi } from '../../patients.api.js';
import { trialsApi } from '../../../trials/trials.api.js';
import { trialQueryKeys } from '../../../trials/trials.query-keys.js';

/**
 * #39 — the two-panel assignment window (Piotr's mockup
 * `tmp/przypisywanie-wizyt.png`, decisions i–vi of the #39 preflight):
 * left = the visits of the participation, right = the catalog positions NOT
 * yet assigned (T5: a position lives in the schedule at most once, so the
 * right panel excludes the assigned ones and the server backs that with 409).
 *
 * Writes are AUTOSAVED — every transfer is one ALL-OR-NOTHING request
 * (`schedule/assign` / `schedule/unassign`) fired on click; there is no
 * „Zapisz” button, the footer only says so and offers ZAMKNIJ. A failed
 * transfer changes nothing (the backend rolls the batch back) and surfaces
 * as a banner with the checks kept for a retry.
 *
 * B7 stays enforced in the UI: a performed visit (dateReal set) cannot be
 * unchecked into the right panel — its checkbox is disabled and locked.
 */
export interface AssignVisitsDialogProps {
  /** The chart owning the participation (`patients.id`). */
  patientId: number;
  /** The participation whose schedule changes (`trial_enrollments.id`). */
  enrollmentId: number;
  /** The active trial — feeds the group dropdowns (`trial_visit_groups`). */
  trialId: number;
  /** Presentation name of the patient (e.g. `Anna Testowa`). */
  patientName: string;
  /** Protocol number of the active trial (e.g. `PROTOCOL-2026-A`). */
  trialLabel: string;
  /** Protocol catalog of the active trial, in protocol order. */
  catalog: TrialVisitDto[];
  /** Current schedule of the participation (the left panel). */
  plan: PatientVisitDto[];
  /** Called after every successful transfer so the parent refreshes caches. */
  onChanged: (kind: 'assign' | 'unassign') => void;
  onClose: () => void;
}

/** Dropdown value of one panel: every row, the ungrouped ones, or one group. */
type GroupFilter = 'all' | 'ungrouped' | number;

const CONTACT_ICONS: Record<TrialVisitContact, typeof Building2> = {
  site: Building2,
  home: Home,
  phone: Phone,
  site_imp: Lock,
};

const CONTACT_LABELS: Record<TrialVisitContact, string> = {
  site: 'Ośrodek',
  home: 'Dom',
  phone: 'Telefon',
  site_imp: 'Ośrodek – wyłącznie podanie leku',
};

/** Legacy channel of a stored visit (S/P/H/I) → the catalog contact vocab. */
const LEGACY_CONTACT: Record<PatientVisitDto['type'], TrialVisitContact> = {
  S: 'site',
  P: 'phone',
  H: 'home',
  I: 'site_imp',
};

/**
 * Stable bar/swatch color per group, derived from the group order (schema
 * comment of `trial_visit_groups.color`: NULL / not-exposed color falls back
 * to the palette). The API does not return the stored color yet, so the
 * palette IS the source until a later atom exposes it; the order (id) keeps
 * the color stable across renders and sessions.
 */
const GROUP_PALETTE = [
  '#38bdf8',
  '#34d399',
  '#a78bfa',
  '#fbbf24',
  '#fb7185',
  '#22d3ee',
  '#a3e635',
  '#f472b6',
] as const;

interface LeftRow {
  visitId: number;
  label: string;
  dateText: string;
  contact: TrialVisitContact;
  comment: string;
  groupId: number | null;
  /** B7: performed visits stay in the left panel forever. */
  performed: boolean;
  order: number;
}

interface RightRow {
  trialVisitId: number;
  label: string;
  kind: string;
  contact: TrialVisitContact;
  description: string;
  groupId: number | null;
  order: number;
}

function contactIcon(contact: TrialVisitContact): JSX.Element {
  const Icon = CONTACT_ICONS[contact];
  return (
    <Icon
      role="img"
      className="h-3.5 w-3.5 shrink-0 text-slate-400"
      aria-label={CONTACT_LABELS[contact]}
    />
  );
}

function groupBar(color: string | null): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3.5 w-1 shrink-0 rounded-sm"
      style={{ backgroundColor: color ?? '#475569' }}
    />
  );
}

function matchesGroup(groupId: number | null, filter: GroupFilter): boolean {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'ungrouped') {
    return groupId === null;
  }
  return groupId === filter;
}

export function AssignVisitsDialog({
  patientId,
  enrollmentId,
  trialId,
  patientName,
  trialLabel,
  catalog,
  plan,
  onChanged,
  onClose,
}: AssignVisitsDialogProps): JSX.Element {
  const [leftChecked, setLeftChecked] = useState<ReadonlySet<number>>(new Set());
  const [rightChecked, setRightChecked] = useState<ReadonlySet<number>>(new Set());
  const [leftFilter, setLeftFilter] = useState<GroupFilter>('all');
  const [rightFilter, setRightFilter] = useState<GroupFilter>('all');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const groupsQuery = useQuery({
    queryKey: trialQueryKeys.visitGroups(trialId),
    queryFn: () => trialsApi.listVisitGroups(trialId),
    staleTime: 5 * 60 * 1000,
  });
  const groups = groupsQuery.data ?? [];
  const groupColor = new Map(
    [...groups]
      .sort((left, right) => left.id - right.id)
      .map((group, index) => [group.id, GROUP_PALETTE[index % GROUP_PALETTE.length] ?? '#475569']),
  );

  const positions = new Map(catalog.map((position) => [position.id, position]));

  const leftRows: LeftRow[] = [...plan]
    .map((visit) => {
      const position = visit.trialVisitId === null ? undefined : positions.get(visit.trialVisitId);
      return {
        visitId: visit.id,
        label: position
          ? `V${position.visitNumber} ${visitLabel(position.visitName1, position.visitName2)}`
          : (visit.presetName ?? '—'),
        dateText: visit.dateReal ?? visit.dateScheduled ?? '—',
        contact: position ? position.contactType : LEGACY_CONTACT[visit.type],
        comment: visit.mainComment ?? '—',
        groupId: position?.groupId ?? null,
        performed: visit.dateReal !== null,
        order: position ? position.visitNumber : Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((left, right) => left.order - right.order || left.visitId - right.visitId);

  const assignedIds = new Set(plan.map((visit) => visit.trialVisitId));
  const rightRows: RightRow[] = [...catalog]
    .filter((position) => !assignedIds.has(position.id))
    .map((position) => ({
      trialVisitId: position.id,
      label: `V${position.visitNumber} ${visitLabel(position.visitName1, position.visitName2)}`,
      kind: position.kind,
      contact: position.contactType,
      description: position.description ?? '—',
      groupId: position.groupId,
      order: position.visitNumber,
    }))
    .sort((left, right) => left.order - right.order || left.trialVisitId - right.trialVisitId);

  const visibleLeft = leftRows.filter((row) => matchesGroup(row.groupId, leftFilter));
  const visibleRight = rightRows.filter((row) => matchesGroup(row.groupId, rightFilter));

  const assignMutation = useMutation({
    mutationFn: (trialVisitIds: number[]) =>
      patientsApi.assignScheduleVisits(patientId, enrollmentId, { trialVisitIds }),
    onSuccess: () => {
      setRightChecked(new Set());
      setErrorMessage(null);
      onChanged('assign');
    },
    onError: async (error: unknown) => {
      setErrorMessage(await getApiErrorMessage(error, 'Nie udało się przypisać wizyt.'));
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (visitIds: number[]) =>
      patientsApi.unassignScheduleVisits(patientId, enrollmentId, { visitIds }),
    onSuccess: () => {
      setLeftChecked(new Set());
      setErrorMessage(null);
      onChanged('unassign');
    },
    onError: async (error: unknown) => {
      setErrorMessage(await getApiErrorMessage(error, 'Nie udało się odpiąć wizyt.'));
    },
  });

  const pending = assignMutation.isPending || unassignMutation.isPending;

  function toggle(
    set: ReadonlySet<number>,
    setter: (next: Set<number>) => void,
    id: number,
  ): void {
    const next = new Set(set);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setter(next);
  }

  function toggleAllVisible(
    rows: { id: number; checkable: boolean }[],
    checked: ReadonlySet<number>,
    setter: (next: Set<number>) => void,
  ): void {
    const ids = rows.filter((row) => row.checkable).map((row) => row.id);
    const allChecked = ids.length > 0 && ids.every((id) => checked.has(id));
    const next = new Set(checked);
    for (const id of ids) {
      if (allChecked) {
        next.delete(id);
      } else {
        next.add(id);
      }
    }
    setter(next);
  }

  function groupSelect(
    value: GroupFilter,
    setter: (next: GroupFilter) => void,
    label: string,
  ): JSX.Element {
    return (
      <label className="flex min-w-0 flex-1 flex-col gap-1 text-[11px] text-slate-400">
        {label}
        <select
          className="rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5 text-xs text-slate-100"
          value={typeof value === 'number' ? String(value) : value}
          disabled={pending}
          onChange={(event) => {
            const raw = event.target.value;
            setter(raw === 'all' || raw === 'ungrouped' ? raw : Number(raw));
          }}
        >
          <option value="all">Wszystkie grupy</option>
          <option value="ungrouped">Bez grupy</option>
          {groups.map((group) => (
            <option key={group.id} value={String(group.id)}>
              {group.name}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const leftSelectable = visibleLeft.map((row) => ({
    id: row.visitId,
    checkable: !row.performed,
  }));
  const leftAllChecked =
    leftSelectable.some((row) => row.checkable) &&
    leftSelectable.filter((row) => row.checkable).every((row) => leftChecked.has(row.id));
  const rightAllChecked =
    visibleRight.length > 0 && visibleRight.every((row) => rightChecked.has(row.trialVisitId));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-visits-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id="assign-visits-title" className="text-sm font-semibold text-slate-100">
              Przypisywanie wizyt do pacjenta
            </h3>
            <p className="mt-1 text-xs text-slate-400">
              {patientName} · badanie {trialLabel}. Zaznacz wizyty i przenieś je strzałką —
              pojedynczo, kilka naraz albo całym filtrem grupy.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij okno przypisywania wizyt"
            className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {errorMessage !== null ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-rose-900/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200"
          >
            {errorMessage} Nic nie zostało zapisane — zaznaczenie pozostało, możesz spróbować
            ponownie.
          </p>
        ) : null}

        <div className="mt-4 flex items-end gap-3">
          {groupSelect(leftFilter, setLeftFilter, 'Grupa')}
          <button
            type="button"
            disabled={pending || leftChecked.size === 0}
            onClick={() => unassignMutation.mutate([...leftChecked])}
            className="rounded-md border border-emerald-700/70 px-4 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-950/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            WSZYSTKIE ZAZNACZONE{leftChecked.size > 0 ? ` (${leftChecked.size})` : ''} →
          </button>
          <button
            type="button"
            disabled={pending || rightChecked.size === 0}
            onClick={() => assignMutation.mutate([...rightChecked])}
            className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ← WSZYSTKIE ZAZNACZONE{rightChecked.size > 0 ? ` (${rightChecked.size})` : ''}
          </button>
          {groupSelect(rightFilter, setRightFilter, 'Grupa')}
        </div>

        <div className="mt-4 grid min-h-0 flex-1 grid-cols-2 gap-4 overflow-auto">
          <section aria-label="Wizyty pacjenta">
            <h4 className="text-[11px] font-semibold tracking-wide text-slate-500">
              WIZYTY PACJENTA
            </h4>
            <table className="mt-1 w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-[11px] tracking-wide text-slate-500">
                  <th scope="col" className="px-2 py-1.5 font-medium">
                    <input
                      type="checkbox"
                      aria-label="Zaznacz wszystkie widoczne wizyty pacjenta"
                      className="cursor-pointer accent-emerald-500"
                      checked={leftAllChecked}
                      disabled={pending || !leftSelectable.some((row) => row.checkable)}
                      onChange={() =>
                        toggleAllVisible(leftSelectable, leftChecked, setLeftChecked)
                      }
                    />
                  </th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Wizyta</th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Data</th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Typ</th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Opis</th>
                </tr>
              </thead>
              <tbody>
                {visibleLeft.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-2 py-3 text-slate-500">
                      Brak wizyt w tym filtrze.
                    </td>
                  </tr>
                ) : (
                  visibleLeft.map((row) => (
                    <tr key={row.visitId} className="border-b border-slate-800/60 text-slate-300">
                      <td className="px-2 py-1.5">
                        {row.performed ? (
                          <Lock
                            role="img"
                            className="h-3.5 w-3.5 text-amber-500"
                            aria-label="Wizyta wykonana — nie można odpiąć"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            aria-label={`Zaznacz ${row.label}`}
                            className="cursor-pointer accent-emerald-500"
                            checked={leftChecked.has(row.visitId)}
                            disabled={pending}
                            onChange={() =>
                              toggle(leftChecked, (next) => setLeftChecked(next), row.visitId)
                            }
                          />
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-slate-100">
                        <span className="flex items-center gap-1.5">
                          {groupBar(row.groupId === null ? null : (groupColor.get(row.groupId) ?? null))}
                          {row.label}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-400">{row.dateText}</td>
                      <td className="px-2 py-1.5">{contactIcon(row.contact)}</td>
                      <td className="px-2 py-1.5 text-slate-400">{row.comment}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          <section aria-label="Wizyty w badaniu">
            <h4 className="text-[11px] font-semibold tracking-wide text-slate-500">
              WIZYTY W BADANIU
            </h4>
            <table className="mt-1 w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-[11px] tracking-wide text-slate-500">
                  <th scope="col" className="px-2 py-1.5 font-medium">
                    <input
                      type="checkbox"
                      aria-label="Zaznacz wszystkie widoczne wizyty badania"
                      className="cursor-pointer accent-emerald-500"
                      checked={rightAllChecked}
                      disabled={pending || visibleRight.length === 0}
                      onChange={() =>
                        toggleAllVisible(
                          visibleRight.map((row) => ({
                            id: row.trialVisitId,
                            checkable: true,
                          })),
                          rightChecked,
                          setRightChecked,
                        )
                      }
                    />
                  </th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Wizyta</th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Rodzaj</th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Typ</th>
                  <th scope="col" className="px-2 py-1.5 font-medium">Opis</th>
                </tr>
              </thead>
              <tbody>
                {visibleRight.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-2 py-3 text-slate-500">
                      Każda pozycja katalogu jest już przypisana albo filtr nie ma pozycji.
                    </td>
                  </tr>
                ) : (
                  visibleRight.map((row) => (
                    <tr
                      key={row.trialVisitId}
                      className="border-b border-slate-800/60 text-slate-300"
                    >
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          aria-label={`Zaznacz ${row.label}`}
                          className="cursor-pointer accent-emerald-500"
                          checked={rightChecked.has(row.trialVisitId)}
                          disabled={pending}
                          onChange={() =>
                            toggle(rightChecked, (next) => setRightChecked(next), row.trialVisitId)
                          }
                        />
                      </td>
                      <td className="px-2 py-1.5 text-slate-100">
                        <span className="flex items-center gap-1.5">
                          {groupBar(row.groupId === null ? null : (groupColor.get(row.groupId) ?? null))}
                          {row.label}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-400">{row.kind}</td>
                      <td className="px-2 py-1.5">{contactIcon(row.contact)}</td>
                      <td className="px-2 py-1.5 text-slate-400">{row.description}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          <span className="text-[11px] text-slate-400">Dane są zapisywane automatycznie</span>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ZAMKNIJ
          </button>
        </div>
      </div>
    </div>
  );
}
